import React, { } from 'react'
import { useTranslation } from 'react-i18next'
import { colors, css, fonts, frames, glyphs } from '../../../Styles'
import { Location, SectionsInfo } from '../../../Services';
import { currentEsfViewTypes, useEsfContext } from '../_EsfWalkThrough/EsfContext';
import ESF_STATION from './EsfStation';
import { EsfLocation, TestData as esfSections, EsfSectionsInfo, StationInfo } from "../../../Services/Agrisys_GqlDataCaller/WalkThroughDataCalls/getEsfStructureCall"
import ProgressBar from '../../ProgressViewer/progressBar';

interface lfPenslist_overrides {
  primaryColor?: colors;
  textColor?: colors;
  fittedWidth?: boolean;
  border?: string;
  attention?: number
}

interface lf_penlist_Props{
  focus_view?: boolean;
  section?: EsfLocation
  popoverContent?: (index: number, station: StationInfo) => JSX.Element;
  overrides?: lfPenslist_overrides | ((index: number) => lfPenslist_overrides);
}
/**
 * SECTION_BASIC
 * @param sectionId Unique identifier for each section.
 * @param sectionType Basic, advanced and single. Each renders different content of animal data.
 * @param onPenClick OnClick event to handle penId.
 * @param onAddBtn OnClick event to handle Add btn.
 * @param onMoveBtn OnClick event to handle Move btn.
 * @param onEditBtn OnClick event to handle Edit btn.
 * @returns a section of pens for Freeda Location Feeder systems.
 */
export default function ESF_STATIONLIST({ focus_view, section, popoverContent, overrides }: lf_penlist_Props) {
  const { t } = useTranslation()
  const { state: { currentStationViewType } } = useEsfContext();
  
  const _framed = currentStationViewType !== currentEsfViewTypes.systemControle

  const stations = section?.stations ?? []
  
  if (!stations || stations.length === 0) return null;

  const maxRowsAndCols = stations.reduce(
    (acc, station) => {
      const [endRow, endCol] = station.visual ? station.visual.split('/').map(Number) : [0, 0];
      return {
        rows: Math.max(acc.rows, endRow),
        cols: Math.max(acc.cols, endCol),
      };
    },
    { rows: 0, cols: 0 }
  );

  const _gridTemplate = `repeat(${maxRowsAndCols.cols}, 1fr)`

  if (stations.length === 0) {
    return <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary, opacity: "0.3", fontStyle: "italic" }}>{t("noData", { defaultValue: "no data" })}</p>;
  }

  const renderContent = () => {
    return stations.map((station, index) => {

      const _overrides = typeof overrides === "function" ? overrides(index) : overrides ?? {};
      const _popoverContent = typeof popoverContent === "function" ? popoverContent(index, station) : popoverContent ?? undefined;

      return (
        <ESF_STATION
          focus_view={focus_view}
          key={station.station_id}
          station={station}
          popoverContent={_popoverContent}
          gridArea={station.visual ? station.visual.replace(/^"|"$/g, '').replace(/^'|'$/g, '') : ""}
          overrides={{
            primaryColor: _overrides.primaryColor,
            textColor: _overrides.textColor,
            width: _overrides.fittedWidth && maxRowsAndCols.cols === 4 ? "56px" : undefined,
            border: _overrides.border,
            attention: _overrides.attention
          }}
        />
      )
    });
  };
  
  return (
    <frames.SectionFrame key={section?.location_id} $isHovered={_framed} style={{ padding: _framed ? "10px 12px 14px 12px" : "0px" }}>
      {_framed ? <p style={{...fonts.header, ...fonts.fontSize3, color: colors.greyDarkPrimary, zIndex: "2"}}>{section?.name}</p> : undefined}
      <div style={{ display: "grid", gridTemplateColumns: _gridTemplate, gridTemplateRows: `repeat(${maxRowsAndCols.rows}, auto)`, width: "fit-content", height: "fit-content", gap: "10px", overflow: "hidden", zIndex: "2" }}>
        <ProgressBar total={100} progressed={70}/>
        {renderContent()}
      </div>
      {_framed ? <div style={{position: "absolute", inset: "0 0 0 0", backgroundColor: colors.greyLightSecondary+"bf", borderRadius: "10px", zIndex: "1"}}/> : undefined}
    </frames.SectionFrame>
  );
}